import React, { useState, useEffect, useRef, useCallback } from 'react';
import { track, getSlug } from '@/utils/tracker';
import DemoExplainer, { InfoTip, FirstVisitBanner } from '@/components/DemoExplainer';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { chroApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import Logo from '@/components/Logo';
import { toast } from 'sonner';
import { Radio, LayoutGrid, Users, LogOut, Sparkles, BarChart2, Layers, LineChart, Shuffle, GitMerge } from 'lucide-react';

/* ═══════════════════════════════════════════
   TOKENS
   ═══════════════════════════════════════════ */
// Design tokens sourced from tokens.css — use var(--token) in JSX styles
// Colour aliases for JS references (mirrors tokens.css)
const C = {
  bg:       '#0A0A1A',
  bg2:      '#0D0D20',
  bg3:      'rgba(255,255,255,0.03)',
  bg4:      'rgba(255,255,255,0.06)',
  text:     '#FFFFFF',
  t2:       'rgba(255,255,255,0.92)',
  t3:       'rgba(255,255,255,0.72)',
  t4:       'rgba(255,255,255,0.48)',
  line:     'rgba(255,255,255,0.10)',
  lines:    'rgba(255,255,255,0.07)',
  bot:      '#00D4C8',
  borrow:   '#06B6D4',
  build:    '#C9A84C',
  bi:       '#E8B96A',
  buy:      '#EC4899',
  teal:     '#00D4C8',
  gold:     '#C9A84C',
  violet:   '#E8B96A',
  pink:     '#EC4899',
  cyan:     '#06B6D4',
  amber:    '#C9A84C',
  sigAmber: '#F59E0B',
  sigRed:   '#FF4444',
  sigGreen: '#10B981',
  muted:    'rgba(255,255,255,0.48)',
  surface:  '#1A1A2E',
  surface2: '#22223E',
  // ── Retained pre-existing keys (used elsewhere in this file) ──
  obsidian: '#03050A',
  card:     '#0B1120',
  border:   'rgba(255,255,255,0.07)',
  white:    '#F2F6FA',
  fog:      '#6B7E91',
  red:      '#FF3B5C',
  green:    '#2EC8A0',
};

const NAV_H = 60;

/* ═══════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════ */
const fmtCur = (v, cur = 'GBP', full = false) => {
  const sym = { GBP: '\u00A3', USD: '$', AED: 'AED\u00A0', INR: '\u20B9', MUR: 'Rs\u00A0' }[cur] || '\u20B9';
  if (!full) {
    if (cur === 'INR') {
      if (v >= 10000000) return `${sym}${(v / 10000000).toFixed(1)}Cr`;
      if (v >= 100000)   return `${sym}${(v / 100000).toFixed(1)}L`;
      if (v >= 1000)     return `${sym}${(v / 1000).toFixed(0)}K`;
    } else {
      if (v >= 1000000000) return `${sym}${(v / 1000000000).toFixed(1)}B`;
      if (v >= 1000000)    return `${sym}${(v / 1000000).toFixed(1)}M`;
      if (v >= 1000)       return `${sym}${(v / 1000).toFixed(0)}K`;
    }
  }
  return `${sym}${(v || 0).toLocaleString()}`;
};

const signalColor = (s) =>
  s === 'red' ? C.red : s === 'amber' ? C.amber : C.green;

/* ═══════════════════════════════════════════
   INLINE KEYFRAME STYLES  (injected once)
   ═══════════════════════════════════════════ */
const STYLE_ID = 'signal-room-kf';
const ensureStyles = () => {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
@keyframes sr-bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(6px)}}
@keyframes sr-pulse{0%,100%{opacity:.15}50%{opacity:.35}}
@keyframes sr-skele{0%{background-position:-200% 0}100%{background-position:200% 0}}
.sr-chevron{animation:sr-bounce 2s ease-in-out infinite}
.sr-skel{background:linear-gradient(90deg,rgba(255,255,255,.04) 25%,rgba(255,255,255,.08) 50%,rgba(255,255,255,.04) 75%);background-size:200% 100%;animation:sr-skele 1.5s ease-in-out infinite;border-radius:6px}
`;
  document.head.appendChild(s);
};

/* ═══════════════════════════════════════════
   ANIMATED COUNTER
   ═══════════════════════════════════════════ */
const Counter = ({ value, currency, duration = 2000 }) => {
  const [disp, setDisp] = useState(0);
  const start = useRef(null);
  const raf = useRef(null);

  useEffect(() => {
    const tick = (ts) => {
      if (!start.current) start.current = ts;
      const p = Math.min((ts - start.current) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 4);
      setDisp(Math.floor(value * ease));
      if (p < 1) raf.current = requestAnimationFrame(tick);
      else track(localStorage.getItem('demo_slug') || '', 'action_taken', 'asbc_counter_completed');
    };
    start.current = null;
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [value, duration]);

  const sym = { GBP: '\u00A3', USD: '$', AED: 'AED\u00A0', INR: '\u20B9', MUR: 'Rs\u00A0' }[currency] || '\u20B9';
  let body;
  if (currency === 'INR') {
    if (disp >= 10000000)      body = `${(disp / 10000000).toFixed(1)}Cr`;
    else if (disp >= 100000)   body = `${(disp / 100000).toFixed(1)}L`;
    else if (disp >= 1000)     body = `${(disp / 1000).toFixed(0)}K`;
    else                        body = disp.toLocaleString();
  } else {
    if (disp >= 1000000000)    body = `${(disp / 1000000000).toFixed(1)}B`;
    else if (disp >= 1000000)  body = `${(disp / 1000000).toFixed(1)}M`;
    else if (disp >= 1000)     body = `${(disp / 1000).toFixed(0)}K`;
    else                        body = disp.toLocaleString();
  }
  return <>{sym}{body}</>;
};

/* ═══════════════════════════════════════════
   MINI-TRENDLINE (pure SVG)
   ═══════════════════════════════════════════ */
const Trendline = ({ points = [], currency }) => {
  if (!points.length) return null;
  const vals = points.map((p) => p.gap || 0);
  const months = points.map((p) => p.month || '');
  const max = Math.max(...vals) * 1.1 || 1;
  const min = Math.min(...vals) * 0.9;
  const w = 640, h = 180, px = 40, py = 24;
  const iw = w - px * 2, ih = h - py * 2;

  const toX = (i) => px + (i / (vals.length - 1)) * iw;
  const toY = (v) => py + ih - ((v - min) / (max - min || 1)) * ih;

  const path = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(v).toFixed(1)}`).join(' ');
  const last = vals.length - 1;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 'auto' }}>
      <defs>
        <linearGradient id="tl-g" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.teal} stopOpacity="0.25" />
          <stop offset="100%" stopColor={C.teal} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* fill area */}
      <path d={`${path} L${toX(last).toFixed(1)},${(py + ih).toFixed(1)} L${toX(0).toFixed(1)},${(py + ih).toFixed(1)} Z`} fill="url(#tl-g)" />
      {/* line */}
      <path d={path} fill="none" stroke={C.teal} strokeWidth="2" />
      {/* end dot */}
      <circle cx={toX(last)} cy={toY(vals[last])} r="5" fill={C.teal} />
      {/* month labels */}
      {months.map((m, i) => (
        i % Math.ceil(months.length / 6) === 0 || i === last ? (
          <text key={i} x={toX(i)} y={h - 2} textAnchor="middle" fill={C.muted} fontSize="10" fontFamily="'Overpass Mono', monospace">{m}</text>
        ) : null
      ))}
    </svg>
  );
};

/* ═══════════════════════════════════════════
   INVESTIGATION PHRASES (randomised on each load)
   ═══════════════════════════════════════════ */
const INVESTIGATION_PHRASES = [
  "We interrogated 47 data points.",
  "We gallivanted across 6 signal sources.",
  "We crunched. Then crunched again.",
  "We cross-examined your sector.",
  "We triangulated. Obsessively.",
  "We rummaged through public data so you don't have to.",
  "We deconstructed your workforce. Methodically.",
  "We sniffed out the signal in the noise.",
  "We ran the numbers. Then ran them again.",
  "We went looking. Here's what we found.",
];

/* ═══════════════════════════════════════════
   SCROLL REVEAL - alternating slide-in via IntersectionObserver
   ═══════════════════════════════════════════ */
const ScrollReveal = ({ direction = 'left', children }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const offset = direction === 'left' ? '-60px' : '60px';
  return (
    <div
      ref={ref}
      style={{
        transform: visible ? 'translateX(0)' : `translateX(${offset})`,
        opacity: visible ? 1 : 0,
        transition: 'transform 0.5s ease-out, opacity 0.5s ease-out',
        willChange: 'transform, opacity',
      }}
    >
      {children}
    </div>
  );
};

/* ═══════════════════════════════════════════
   CAROUSEL (kept for reference, no longer used)
   ═══════════════════════════════════════════ */
const Carousel = ({ children }) => {
  const [idx, setIdx] = useState(0);
  const count = React.Children.count(children);
  const touchRef = useRef(null);

  const go = useCallback((d) => setIdx((i) => Math.max(0, Math.min(count - 1, i + d))), [count]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [go]);

  const onTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchRef.current === null) return;
    const dx = e.changedTouches[0].clientX - touchRef.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchRef.current = null;
  };

  const arr = (dir) => (
    <button
      onClick={() => go(dir)}
      style={{
        position: 'absolute', top: '50%', transform: 'translateY(-50%)',
        [dir === -1 ? 'left' : 'right']: -48,
        width: 36, height: 36, borderRadius: '50%',
        background: 'var(--bg4)', border: `1px solid ${C.border}`,
        color: C.muted, fontSize: 18, cursor: 'pointer',
        display: (dir === -1 && idx === 0) || (dir === 1 && idx === count - 1) ? 'none' : 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      {dir === -1 ? '\u2039' : '\u203A'}
    </button>
  );

  return (
    <div style={{ position: 'relative', maxWidth: 800, margin: '0 auto' }}>
      {arr(-1)}
      {arr(1)}
      <div style={{ overflow: 'hidden', borderRadius: 16, width: '100%' }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div style={{
          display: 'flex',
          width: '100%',
          transform: `translateX(-${idx * 100}%)`,
          transition: 'transform 300ms ease-in-out',
        }}>
          {React.Children.map(children, (child, i) => (
            <div key={i} style={{ minWidth: '100%', width: '100%', flexShrink: 0, boxSizing: 'border-box' }}>{child}</div>
          ))}
        </div>
      </div>
      {/* dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
        {Array.from({ length: count }, (_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            style={{
              width: i === idx ? 24 : 8, height: 8, borderRadius: 4,
              background: i === idx ? C.teal : 'rgba(255,255,255,0.15)',
              border: 'none', cursor: 'pointer', transition: 'all 0.3s',
            }}
          />
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   SIGNAL SECTION (open layout, left accent border)
   ═══════════════════════════════════════════ */
const SCard = ({ num, label, headline, note, tooltip, glass, children }) => (
  <div className={glass ? 'glass' : undefined} style={{
    borderLeft: '3px solid var(--teal)',
    padding: '32px 0 32px 32px',
    borderRadius: glass ? 12 : undefined,
    display: 'flex', flexDirection: 'column',
  }}>
    <p style={{ fontFamily: 'var(--fm)', fontSize: 13, letterSpacing: '0.12em', color: 'var(--teal)', marginBottom: 16, textTransform: 'uppercase', fontWeight: 600 }}>
      SIGNAL {String(num).padStart(2, '0')} &middot; {label}
      {tooltip && (
        <span style={{ letterSpacing: 'normal', textTransform: 'none', marginLeft: 6 }}>
          <InfoTip position="right" width={280} title={tooltip.title} text={tooltip.text} />
        </span>
      )}
    </p>
    <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2, marginBottom: 20, letterSpacing: '-0.01em', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      {headline}
    </h3>
    <div>{children}</div>
    {note && (
      <p style={{ fontFamily: 'var(--fm)', fontSize: 12, color: 'var(--t4)', marginTop: 20, lineHeight: 1.6 }}>
        {note}
      </p>
    )}
  </div>
);

/* ═══════════════════════════════════════════
   TOP NAV BAR (extracted from ChroLayout)
   ═══════════════════════════════════════════ */
const TopNav = ({ orgName }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const dailyNav = [
    { label: 'Signal Room', icon: Radio, path: '/signal-room' },
    { label: 'Intelligence', icon: BarChart2, path: '/intelligence' },
    { label: 'Blueprint Room', icon: LayoutGrid, path: '/blueprint/cockpit' },
    { label: 'The Stage', icon: Users, path: '/stage' },
    { label: 'Showcase', icon: Sparkles, path: '/showcase/ai-tools' },
  ];
  const flowNav = [
    { label: 'Deconstruct', icon: Layers, path: '/deconstruct' },
    { label: 'Analyze', icon: LineChart, path: '/analyze' },
    { label: 'Sort · 4B', icon: Shuffle, path: '/sort' },
    { label: 'Recompose', icon: GitMerge, path: '/recompose' },
  ];
  const renderNavLink = (item) => {
    const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    return (
      <Link key={item.path} to={item.path}
        className={cn("flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
          active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        )}
      >
        <item.icon className="h-4 w-4" />{item.label}
      </Link>
    );
  };

  return (
    <header style={{ height: NAV_H, position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, background: 'rgba(3,5,10,0.85)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', height: '100%', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <Link to="/signal-room"><Logo size="nav" /></Link>
          <nav style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {dailyNav.map(renderNavLink)}
            <span aria-hidden="true" style={{ display: 'inline-block', width: 1, height: 18, background: 'var(--line)', margin: '0 8px' }} />
            {flowNav.map(renderNavLink)}
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {orgName && <span style={{ fontSize: 13, color: C.muted }}>{orgName}</span>}
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,212,200,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.teal }}>{user?.name?.charAt(0) || 'C'}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} className="text-muted-foreground">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
const SignalRoom = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [randomPhrase] = useState(() => INVESTIGATION_PHRASES[Math.floor(Math.random() * INVESTIGATION_PHRASES.length)]);

  useEffect(() => {
    const api = window.__particles;
    if (!api) return;
    api.setMode('cluster');
    return () => api.setMode('ambient');
  }, []);

  useEffect(() => { ensureStyles(); }, []);

  const _trackSlug = getSlug();
  const _trackEntry = useRef(Date.now());

  useEffect(() => {
    track(_trackSlug, 'screen_viewed', 'signal_room');
    return () => {
      const dur = Math.round((Date.now() - _trackEntry.current) / 1000);
      track(_trackSlug, 'screen_exited', 'signal_room', dur);
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Demo path v2 - use pre-loaded localStorage data including currency
    //
    // Cache invalidation (V1 Bug 1 fix):
    //   The localStorage demo_signal_data cache is written by
    //   DemoEntry.handleEnter() on demo entry. Without freshness
    //   checks, two failure modes leak:
    //     (a) Cross-demo contamination — entering demo B in another
    //         tab overwrites demo A's cache; tab A then renders B's
    //         signals under A's URL on refresh.
    //     (b) Server-side seed updates — when a demo's signal_room_data
    //         is updated server-side (e.g. ai_signals repopulated),
    //         users with a warm cache never see the new content until
    //         they re-enter via /demo/{slug}.
    //   The fix renders cached data immediately (preserves fast paint)
    //   then, if the cache is stale (slug mismatch or older than TTL),
    //   fetches /demo/{slug} in the background and swaps. Public
    //   endpoint, no auth needed. Fetch failures keep stale data
    //   silently — matches the existing "no toast on demo errors"
    //   pattern.
    const isDemo = localStorage.getItem('is_demo') === 'true';
    if (isDemo) {
      const buildDemoState = (signalData) => ({
        org_name: localStorage.getItem('demo_org_name') || 'Demo Organisation',
        currency: signalData.currency || (() => {
          const org = localStorage.getItem('demo_org_name') || '';
          const sector = localStorage.getItem('demo_sector') || '';
          if (['pearl', 'virtuosic', 'acg', 'godrej', 'prism', 'mahindra'].some(k => org.toLowerCase().includes(k))) return 'INR';
          if (['philip morris', 'pmi'].some(k => org.toLowerCase().includes(k))) return 'USD';
          if (sector === 'manufacturing') return 'INR';
          return 'AED';
        })(),
        asbc_headline: signalData.asbc_optimal || 0,
        asbc_gap: signalData.asbc_gap || 0,
        estimated_labour_cost: signalData.estimated_labour_cost || 0,
        confirmed_headcount: signalData.headcount || 0,
        confidence_score: signalData.confidence_score || 0,
        cost_of_inaction_quarterly: signalData.velocity_quarterly || 0,
        trendline: [],
        functions: (signalData.functions || []).map(f => ({
          ...f,
          signal_state: f.signal,
        })),
        peer_comparison: (signalData.peer_comparison || []).map(p => ({
          ...p,
          gap_percentage: p.gap_pct,
        })),
        founded_year: signalData.founded_year || null,
        revenue_band: signalData.revenue_band || null,
        headquarters: signalData.headquarters || null,
        workforce_news: signalData.workforce_news || [],
        ai_signals: signalData.ai_signals || [],
        shareable_links: [],
      });

      try {
        const raw = localStorage.getItem('demo_signal_data');
        const signalData = raw ? JSON.parse(raw) : {};
        setData(buildDemoState(signalData));
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }

      // Background freshness check. Stale = slug stamp missing, ts
      // stamp missing, slug mismatch (cross-demo contamination), or
      // cache older than TTL.
      const TTL_MS = 10 * 60 * 1000;
      const currentSlug = localStorage.getItem('demo_slug') || '';
      const cachedSlug = localStorage.getItem('demo_signal_data_slug') || '';
      const cachedTs = parseInt(localStorage.getItem('demo_signal_data_ts') || '0', 10);
      const isStale =
        !cachedSlug ||
        !cachedTs ||
        cachedSlug !== currentSlug ||
        (Date.now() - cachedTs) > TTL_MS;

      if (isStale && currentSlug) {
        const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
        fetch(`${apiBase}/demo/${currentSlug}`)
          .then(r => (r.ok ? r.json() : null))
          .then(fresh => {
            if (!fresh || !fresh.signal_room_data) return;
            // Re-write the cache so the next visit reads fresh data
            // without another network round-trip, and stamp the slug
            // + timestamp so the freshness check converges.
            try {
              localStorage.setItem('demo_signal_data', JSON.stringify(fresh.signal_room_data));
              localStorage.setItem('demo_signal_data_slug', currentSlug);
              localStorage.setItem('demo_signal_data_ts', String(Date.now()));
              if (fresh.org_name) localStorage.setItem('demo_org_name', fresh.org_name);
              if (fresh.sector) localStorage.setItem('demo_sector', fresh.sector);
            } catch { /* localStorage write failures non-fatal */ }
            setData(buildDemoState(fresh.signal_room_data));
          })
          .catch(() => { /* keep stale silently — matches demo-error pattern */ });
      }
      return;
    }

    // Normal CHRO path
    (async () => {
      try {
        const res = await chroApi.getSignalRoom();
        setData(res.data);
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          navigate('/login');
          return;
        }
        toast.error(err.response?.data?.detail || 'Failed to load Signal Room');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await chroApi.createShareableLink();
      const url = `${window.location.origin}/shared/${res.data.link_id}`;
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard', { description: `Expires ${new Date(res.data.expires_at).toLocaleDateString()}` });
    } catch {
      toast.error('Failed to create share link');
    } finally {
      setSharing(false);
    }
  };

  const cur = data?.currency || 'GBP';
  const sym = { GBP: '\u00A3', USD: '$', AED: 'AED\u00A0', INR: '\u20B9', MUR: 'Rs\u00A0' }[cur] || '\u20B9';

  /* BG layers */
  const bgBase = C.obsidian;
  const scanline = 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)';
  const noiseSvg = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`;
  const bgStyle = { background: `${scanline}, ${bgBase}`, position: 'relative' };

  /* Skeleton block */
  const Skel = ({ w = '100%', h = 24, mb = 0 }) => (
    <div className="sr-skel" style={{ width: w, height: h, marginBottom: mb }} />
  );

  // Derived data for cards
  const orgName = data?.org_name || '';
  const headcount = data?.confirmed_headcount || 0;
  const labourCost = data?.estimated_labour_cost || 0;
  const gap = data?.asbc_gap || 0;
  // ASBC Optimal = current cost minus the identified gap
  const asbcOpt = Math.max(labourCost - gap, 0);
  const funcCount = data?.functions?.length || 0;
  const peers = data?.peer_comparison || [];
  const trendline = data?.trendline || [];
  const functions = data?.functions || [];
  const costQ = data?.cost_of_inaction_quarterly || 0;
  const confidence = data?.confidence_score || 0;
  const confLabel = confidence >= 80 ? 'High' : confidence >= 60 ? 'Good' : confidence >= 40 ? 'Moderate' : 'Low';

  // Gap as pct of labour cost
  const gapPct = labourCost > 0 ? ((gap / labourCost) * 100).toFixed(1) : '0';

  // Normalise peer values — accept gap_percentage or gap_pct, coerce to Number
  const peerPcts = peers
    .map((p) => {
      const raw = p.gap_percentage != null ? p.gap_percentage : p.gap_pct;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    })
    .filter((v) => v != null);
  const maxPeerPct = peerPcts.length ? Math.max(...peerPcts) : 0;
  const medianPeer = peerPcts.length ? [...peerPcts].sort((a, b) => a - b)[Math.floor(peerPcts.length / 2)] : null;
  const orgGap = labourCost > 0 ? (gap / labourCost) * 100 : null;
  const diff = (orgGap != null && medianPeer != null) ? orgGap - medianPeer : null;
  const aboveMedianValid = diff != null && Number.isFinite(diff);
  const aboveMedian = aboveMedianValid ? Math.abs(Math.round(diff)) : null;
  const aboveMedianNum = diff || 0;

  // Top 6 functions sorted by gap
  const topFuncs = [...functions].sort((a, b) => (b.gap || 0) - (a.gap || 0)).slice(0, 6);
  const attentionCount = functions.filter((f) => f.signal_state === 'red' || f.signal_state === 'amber').length;

  // Signal 07 - Company Intelligence (enrichment data)
  const foundedYear = data?.founded_year || null;
  const revenueBand = data?.revenue_band || null;
  const headquarters = data?.headquarters || null;
  const workforceNews = Array.isArray(data?.workforce_news) ? data.workforce_news : [];
  const aiSignals = Array.isArray(data?.ai_signals) ? data.ai_signals : [];
  const hasIntel = workforceNews.length > 0 || aiSignals.length > 0;
  const totalSignals = hasIntel ? 7 : 6;
  const sentimentDot = (s) => {
    const v = (s || '').toLowerCase();
    if (v === 'positive') return C.green;
    if (v === 'negative') return C.red;
    return C.amber;
  };

  // Error state: API returned nothing
  if (!loading && !data) {
    return (
      <div style={{ background: C.obsidian, color: C.white, fontFamily: "'Inter', sans-serif", minHeight: '100vh' }}>
        <TopNav orgName="" />
        <div style={{ marginTop: NAV_H, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: `calc(100vh - ${NAV_H}px)`, padding: '0 24px', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(232,197,71,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
            <span style={{ fontSize: 24 }}>&#9888;</span>
          </div>
          <h2 style={{ fontFamily: 'var(--fg)', fontSize: 32, marginBottom: 8 }}>Signal Room Not Ready</h2>
          <p style={{ color: C.muted, fontSize: 15, maxWidth: 420, lineHeight: 1.6, marginBottom: 24 }}>
            Your organisation data is still being prepared, or you may need to log in again.
          </p>
          <button
            onClick={() => navigate('/login')}
            style={{ padding: '12px 28px', fontSize: 14, fontWeight: 600, background: C.teal, color: C.obsidian, border: 'none', borderRadius: 8, cursor: 'pointer' }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...bgStyle, color: C.white, fontFamily: "'Inter', sans-serif", minHeight: '100vh' }}>
      {/* Noise overlay */}
      <div style={{ position: 'fixed', inset: 0, backgroundImage: noiseSvg, backgroundRepeat: 'repeat', backgroundSize: 256, pointerEvents: 'none', zIndex: 0 }} />

      <TopNav orgName={orgName} />

      {/* === LAYER 1 - THE HOOK === */}
      <section style={{ marginTop: NAV_H, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', position: 'relative', zIndex: 1, padding: '80px 24px 16px' }}>
        <div className="glass-light" style={{ padding: 40, borderRadius: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 720, width: '100%' }}>
        {/* Org label */}
        <div className="eyebrow" style={{ marginBottom: 48 }}>
          {loading ? <Skel w={280} h={14} /> : <>{orgName} &nbsp;&middot;&nbsp; WORKFORCE INTELLIGENCE</>}
        </div>

        {/* Main figure */}
        <p style={{ fontFamily: 'var(--fm)', fontSize: 13, color: C.muted, letterSpacing: '0.08em', marginBottom: 12, textTransform: 'uppercase' }}>
          Annual transformation potential <span style={{ textTransform: 'none', letterSpacing: 'normal' }}><InfoTip position="bottom" width={300} title="Annual transformation potential" text="The estimated annual cost difference between your current workforce structure and an optimally designed one, priced task by task using ASBC methodology." /></span>
        </p>

        {loading ? (
          <div style={{ marginBottom: 16 }}><Skel w={420} h={72} /></div>
        ) : (
          <h1 className="gap-num" style={{ fontSize: 'clamp(56px, 10vw, 96px)', color: C.white, margin: '0 0 16px' }} data-testid="asbc-headline">
            <Counter value={gap} currency={cur} />
          </h1>
        )}

        <div style={{ fontFamily: 'var(--fm)', fontSize: 11, color: C.fog, marginBottom: 24, letterSpacing: '0.06em' }}>
          {loading ? <Skel w={260} h={12} /> : <>Directional estimate &plusmn;25% &nbsp;&middot;&nbsp; ASBC-computed <InfoTip position="right" width={300} title="ASBC-computed" text="Activity-Skills Based Costing prices individual tasks - not roles - across 6 workforce types: FTE, part-time, contractor, gig worker, bot/AI agent, and fractional executive." /></>}
        </div>

        {/* Velocity */}
        {!loading && costQ > 0 && (
          <p style={{ fontFamily: 'var(--fm)', fontSize: 13, color: C.amber, marginBottom: 16 }}>
            &uarr; +{fmtCur(costQ, cur)} per quarter if no action taken <InfoTip position="right" width={280} title="Cost of inaction" text="The cost of inaction. This gap compounds quarterly as AI adoption accelerates in your sector and peers restructure ahead of you." />
          </p>
        )}

        {/* Confidence */}
        {!loading && (
          <span className={`sig-band ${confidence >= 60 ? 'sb-green' : confidence >= 40 ? 'sb-amber' : 'sb-red'}`}>
            <span className="sb-dot"></span>
            Signal: {confLabel} &middot; {confidence}
            <InfoTip position="right" width={300} title="Signal confidence" text="Confidence score based on data completeness, OCF match accuracy, and source quality. 90-100: Excellent . 70-89: Good . 50-69: Fair . Below 50: Indicative only." />
          </span>
        )}

        {/* Scroll chevron */}
        <div style={{ marginTop: 16 }}>
          <svg className="sr-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.fog} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        </div>
      </section>

      {/* === LAYER 2 - THE SIGNALS === */}
      <section style={{ padding: '32px 24px 80px', position: 'relative', zIndex: 1 }}>
        {/* Investigation headline (randomised) */}
        <div className="glass-light" style={{ maxWidth: 800, margin: '0 auto 32px', textAlign: 'center', padding: '24px 32px', borderRadius: 14 }}>
          <p style={{ fontFamily: 'var(--fm)', fontSize: 12, letterSpacing: '0.14em', color: 'var(--teal)', textTransform: 'uppercase', marginBottom: 12, fontWeight: 600 }}>
            {totalSignals} Signals
          </p>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
            {randomPhrase}
          </h2>
        </div>

        {loading ? (
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 40 }}>
              <Skel w={160} h={12} mb={20} />
              <Skel w="80%" h={32} mb={20} />
              <Skel w="100%" h={120} mb={16} />
              <Skel w={240} h={10} />
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 32 }}>
            {/* CARD 1 - Organisation */}
            <ScrollReveal direction="left">
            <SCard glass num={1} label="ORGANISATION PROFILE" headline={orgName} note="Sourced from public data &middot; Companies House &middot; LinkedIn" tooltip={{ title: 'Organisation Profile', text: 'Sourced from public data: Companies House, LinkedIn, regulatory filings, and job board activity.' }}>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 8 }}>
                {[
                  [headcount.toLocaleString(), 'Headcount'],
                  [fmtCur(labourCost, cur), 'Labour Cost'],
                  [funcCount, 'Functions mapped'],
                ].map(([val, label]) => (
                  <div key={label}>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 52, fontWeight: 700, color: 'var(--text)', lineHeight: 1.1, letterSpacing: '-0.02em' }}>{val}</p>
                    <p style={{ fontFamily: 'var(--fm)', fontSize: 13, color: 'var(--t3)', marginTop: 6 }}>{label}</p>
                  </div>
                ))}
              </div>
            </SCard>
            </ScrollReveal>

            {/* CARD 2 - The Gap */}
            <ScrollReveal direction="right">
            <SCard glass num={2} label="ASBC COMPUTATION" headline={`${fmtCur(gap, cur)} transformation gap identified`} note="ASBC = Activity-Skills Based Costing &middot; task-level pricing across 6 workforce types" tooltip={{ title: 'ASBC Computation', text: 'Each role is deconstructed into tasks. Every task is priced across 6 workforce types. The gap is the difference between current cost and optimal cost.' }}>
              <div style={{ marginTop: 8 }}>
                {/* ASBC Explainer */}
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
                  A different way to value your workforce.
                </p>
                <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 16, color: 'var(--t2)', lineHeight: 1.6, marginBottom: 10 }}>
                  Traditional benchmarks price roles. A Finance Manager costs {sym}X. ASBC (Activity-Skills Based Costing) prices tasks. It asks which tasks genuinely need a senior hire, which could be AI, fractional, or redesigned.
                </p>
                <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 16, color: 'var(--t2)', lineHeight: 1.6, marginBottom: 10 }}>
                  When you price at task level across six workforce types, the gap between what you spend and what is optimal becomes visible for the first time.
                </p>
                <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 16, color: 'var(--t2)', lineHeight: 1.6, marginBottom: 20 }}>
                  That gap is your transformation potential.
                </p>
                <div style={{ height: 1, background: 'var(--line)', marginBottom: 16 }} />
                {/* Current bar */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontFamily: 'var(--fm)', fontSize: 13, color: 'var(--t3)' }}>Current Cost</span>
                    <span style={{ fontFamily: 'var(--fm)', fontSize: 13, color: 'var(--t3)' }}>{fmtCur(labourCost, cur)}</span>
                  </div>
                  <div style={{ height: 12, background: 'rgba(255,255,255,0.08)', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ width: '100%', height: '100%', background: C.fog, borderRadius: 6 }} />
                  </div>
                </div>
                {/* Optimal bar */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontFamily: 'var(--fm)', fontSize: 13, color: 'var(--t3)' }}>ASBC Optimal</span>
                    <span style={{ fontFamily: 'var(--fm)', fontSize: 13, color: C.teal }}>{fmtCur(asbcOpt, cur)}</span>
                  </div>
                  <div style={{ height: 12, background: 'rgba(255,255,255,0.08)', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ width: labourCost > 0 ? `${(asbcOpt / labourCost * 100).toFixed(1)}%` : '0%', height: '100%', background: C.teal, borderRadius: 6, transition: 'width 0.6s' }} />
                  </div>
                </div>
                {/* Gap arrow */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                  <span style={{ fontFamily: 'var(--fm)', fontSize: 14, color: C.amber }}>&darr;</span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 26, fontWeight: 700, color: C.amber }}>{fmtCur(gap, cur)}</span>
                  <span style={{ fontFamily: 'var(--fm)', fontSize: 13, color: 'var(--t3)' }}>gap</span>
                </div>
              </div>
            </SCard>
            </ScrollReveal>

            {/* CARD 3 - By Function */}
            <ScrollReveal direction="left">
            <SCard glass num={3} label="FUNCTION SIGNALS" headline={`${funcCount} functions mapped · ${attentionCount} require attention`} note="Red = significant gap · Amber = moderate · Green = well-aligned" tooltip={{ title: 'Function Signals', text: 'Red = transformation gap above 20% of function labour cost. Amber = 10-20%. Green = below 10% - well-aligned with optimal workforce structure.' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
                {topFuncs.map((f) => (
                  <div key={f.name} style={{
                    padding: '10px 18px', borderRadius: 99,
                    background: `${signalColor(f.signal_state)}18`,
                    border: `1px solid ${signalColor(f.signal_state)}40`,
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: signalColor(f.signal_state), display: 'inline-block' }} />
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{f.name}</span>
                    <span style={{ fontFamily: 'var(--fm)', fontSize: 13, color: 'var(--t3)' }}>{fmtCur(f.gap || 0, cur)}</span>
                  </div>
                ))}
              </div>
            </SCard>
            </ScrollReveal>

            {/* CARD 4 - Trendline */}
            <ScrollReveal direction="right">
            <SCard glass num={4} label="12-MONTH TRAJECTORY" headline={`Gap growing at ${fmtCur(costQ, cur)} per quarter`} note="Cost of inaction compounds quarterly without transformation action" tooltip={{ title: '12-Month Trajectory', text: 'The transformation gap grows as AI capability expands and competitors restructure. Velocity shows how fast your gap is widening.' }}>
              <div style={{ marginTop: 12 }}>
                {trendline && trendline.length > 0 ? (
                  <Trendline points={trendline} currency={cur} />
                ) : (
                  <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 16, color: 'var(--t3)', lineHeight: 1.6, margin: 0 }}>
                    Trajectory data builds after your first analysis session.
                  </p>
                )}
              </div>
            </SCard>
            </ScrollReveal>

            {/* CARD 5 - Peer Comparison */}
            <ScrollReveal direction="left">
            <SCard glass num={5} label="SECTOR BENCHMARKS" headline={(() => {
              if (aboveMedian == null) return `${orgName} gap vs sector median`;
              if (Math.abs(aboveMedianNum) < 1) return `${orgName} is at sector median`;
              // Higher gap % = worse performance = behind
              if (aboveMedianNum > 0) return `${orgName} is ${aboveMedian}% behind sector median`;
              return `${orgName} is ${aboveMedian}% ahead of sector median`;
            })()} note="ASBC gap as % of total labour cost" tooltip={{ title: 'Sector Benchmarks', text: 'Peer gap percentages are directional estimates based on public data, job board signals, and sector AI adoption rates.' }}>
              <div style={{ marginTop: 8 }}>
                {peers.map((p) => {
                  const raw = p.gap_percentage != null ? p.gap_percentage : p.gap_pct;
                  const n = Number(raw);
                  const pct = Number.isFinite(n) ? n : 0;
                  const barWidth = maxPeerPct > 0 ? (pct / maxPeerPct) * 100 : 0;
                  return (
                    <div key={p.name} className="peer-row">
                      <span className="peer-name">{p.name}</span>
                      <div className="peer-bg">
                        <div className="peer-fill" style={{ width: `${Math.min(barWidth, 100)}%`, background: C.fog }} />
                      </div>
                      <span className="peer-pct">{pct.toFixed(1)}%</span>
                    </div>
                  );
                })}
                {/* Acme (you) row */}
                {(() => {
                  const ownPct = Number(gapPct) || 0;
                  const ownBar = maxPeerPct > 0 ? Math.min((ownPct / maxPeerPct) * 100, 100) : Math.min(ownPct, 100);
                  return (
                    <div className="peer-row" style={{ marginTop: 8, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                      <span className="peer-name" style={{ color: 'var(--teal)', fontWeight: 700 }}>{orgName} (you)</span>
                      <div className="peer-bg">
                        <div className="peer-fill" style={{ width: `${ownBar}%`, background: 'var(--teal)' }} />
                      </div>
                      <span className="peer-pct" style={{ color: 'var(--teal)', fontWeight: 700 }}>{ownPct.toFixed(1)}%</span>
                    </div>
                  );
                })()}
              </div>
            </SCard>
            </ScrollReveal>

            {/* CARD 6 - What This Means */}
            <ScrollReveal direction="right">
            <SCard glass num={6} label="INSIGHT" headline={`${fmtCur(gap, cur)} is within reach`} tooltip={{ title: 'Insight', text: 'This is a transformation number, not a cost-cutting number. It represents the opportunity to redesign work - routing tasks to their optimal workforce type.' }}>
              <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: 'var(--t2)', lineHeight: 1.65, marginTop: 8 }}>
                This is not a cost-cutting number. It is a transformation number: the gap between how your workforce is currently structured and how it could be structured using AI, fractional talent, and inclusion pathways.
              </p>
            </SCard>
            </ScrollReveal>

            {/* CARD 7 - Company Intelligence (conditional on enrichment data) */}
            {hasIntel && (
              <ScrollReveal direction="left">
              <SCard glass num={7} label="COMPANY INTELLIGENCE" headline={`${orgName} — AI transformation signals detected`} note="Sourced from real-time web search · Claude-enriched" tooltip={{ title: 'Company Intelligence', text: 'Sourced from real-time web search across news databases, company filings, and public announcements. AI signals indicate active workforce transformation activity. Updated at each enrichment run.' }}>
                {/* Company profile strip */}
                {(foundedYear || headquarters || revenueBand || headcount > 0) && (
                  <p style={{ fontFamily: 'var(--fm)', fontSize: 13, color: 'var(--t3)', marginTop: 8, marginBottom: 24, lineHeight: 1.6 }}>
                    {[
                      foundedYear && `Founded ${foundedYear}`,
                      headquarters,
                      revenueBand && `Revenue ${revenueBand}`,
                      headcount > 0 && `${headcount.toLocaleString()} employees`,
                    ].filter(Boolean).join(' · ')}
                  </p>
                )}

                {/* AI & Workforce Signals */}
                {aiSignals.length > 0 && (
                  <div style={{ marginBottom: 28 }}>
                    <p style={{ fontFamily: 'var(--fm)', fontSize: 12, letterSpacing: '0.1em', color: C.teal, textTransform: 'uppercase', marginBottom: 12, fontWeight: 600 }}>
                      AI & Workforce Signals
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, borderLeft: '2px solid rgba(0,212,200,0.5)' }}>
                      {aiSignals.map((sig, i) => (
                        <li key={i} style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: 'var(--t2)', lineHeight: 1.6, padding: '6px 0 6px 16px' }}>
                          {typeof sig === 'string' ? sig : (sig?.text || sig?.headline || '')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recent Workforce News */}
                {workforceNews.length > 0 && (
                  <div>
                    <p style={{ fontFamily: 'var(--fm)', fontSize: 12, letterSpacing: '0.1em', color: C.teal, textTransform: 'uppercase', marginBottom: 12, fontWeight: 600 }}>
                      Recent Workforce News
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {workforceNews.map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: sentimentDot(item?.sentiment), flexShrink: 0, marginTop: 8 }} />
                          <div style={{ flex: 1 }}>
                            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: 'var(--text)', lineHeight: 1.5, margin: 0 }}>
                              {typeof item === 'string' ? item : (item?.headline || item?.text || '')}
                            </p>
                            {item?.date && (
                              <p style={{ fontFamily: 'var(--fm)', fontSize: 12, color: 'var(--t4)', marginTop: 4, marginBottom: 0 }}>
                                {item.date}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </SCard>
              </ScrollReveal>
            )}
          </div>
        )}
      </section>

      {/* === LAYER 3 - BOTTOM CTA === */}
      <section style={{ padding: '80px 24px 120px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <ScrollReveal direction="left">
        <h2 style={{ fontFamily: 'var(--fg)', fontSize: 'clamp(32px, 5vw, 52px)', color: C.white, marginBottom: 32, letterSpacing: '0.02em' }}>
          Ready to deconstruct your roles? &rarr;
        </h2>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/analysis')}
            style={{
              padding: '16px 36px', fontSize: 15, fontWeight: 700,
              background: C.gold, color: C.obsidian, border: 'none',
              borderRadius: 10, cursor: 'pointer', fontFamily: "'Inter', sans-serif",
              boxShadow: `0 0 24px ${C.gold}40`,
            }}
          >
            Analyse your roles &rarr;
          </button>
          <button
            onClick={handleShare}
            disabled={sharing}
            style={{
              padding: '16px 36px', fontSize: 15, fontWeight: 600,
              background: 'transparent', border: `1.5px solid ${C.border}`, color: C.muted,
              borderRadius: 10, cursor: 'pointer', fontFamily: "'Inter', sans-serif",
              opacity: sharing ? 0.5 : 1, transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.teal; e.currentTarget.style.color = C.teal; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
          >
            {sharing ? 'Creating link...' : 'Share this signal \u2192'}
          </button>
        </div>
        </ScrollReveal>
      </section>
      <FirstVisitBanner />
        <DemoExplainer screen="SIGNAL_ROOM" />
    </div>
  );
};

export default SignalRoom;
